import { jsx, jsxs } from 'react/jsx-runtime';
import { useMemo } from 'react';
import styled from 'styled-components';
import { ChevronDownIcon } from '@minuthq/meatball-ui-icons';
import { InputContainer } from '../input-container/input-container.js';
import { canShowLabel } from '../input-container/input-container-utils.js';
import { Text } from '../text/text.js';

function Select({ label, options, value, onChange, required, error, disabled, helperText, startAdornment, ariaLabel, requiredIndicator, size = "medium", ...props }) {
    const flattenedOptions = useMemo(() => {
        return flattenOptions(options);
    }, [options]);
    const selectedLabel = useMemo(() => {
        return (flattenedOptions.find((option) => option.value === value)?.label || "");
    }, [flattenedOptions, value]);
    const optionsContent = useMemo(() => {
        return options.map((option) => {
            if (isGroup(option)) {
                return (jsx("optgroup", { label: option.title, children: option.options.map((groupedOption) => (jsx("option", { value: groupedOption.value, disabled: groupedOption.disabled, hidden: groupedOption.hidden, children: groupedOption.label }, groupedOption.key || groupedOption.value))) }, option.key || option.title));
            }
            return (jsx("option", { value: option.value, disabled: option.disabled, hidden: option.hidden, children: option.label }, option.key || option.value));
        });
    }, [options]);
    return (jsx(InputContainer, { ...(canShowLabel(size)
            ? {
                label,
                size,
            }
            : {
                size,
            }), endAdornment: jsx(ChevronDownIcon, { width: 16, color: "unset" }), error: error, disabled: disabled, helperText: helperText, startAdornment: startAdornment, requiredIndicator: requiredIndicator, shrink: !!selectedLabel, children: jsxs(Wrapper, { children: [jsx(StyledSelect, { value: value, onChange: (e) => {
                        onChange(e.target.value);
                    }, required: required, disabled: disabled, "aria-label": ariaLabel || label, "aria-errormessage": typeof error === "string" ? error : undefined, "aria-invalid": !!error, ...props, children: optionsContent }), jsx(StyledMText, { color: "unset", children: selectedLabel })] }) }));
}
function isGroup(option) {
    return "title" in option;
}
function flattenOptions(options) {
    return options.flatMap((option) => {
        if (isGroup(option)) {
            return option.options;
        }
        return option;
    });
}
const StyledSelect = styled.select `
  all: unset;
  appearance: none;
  opacity: 0;

  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
const StyledMText = styled(Text) `
  min-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Wrapper = styled.div `
  // This makes sure that the text gets and ellipsis when it overflows
  // even if the select is not full width. For example within a flex container
  display: grid;
`;

export { Select };
